import { Navigate, useParams, useSearchParams } from 'react-router-dom';
import { WorkspaceScopeProvider } from '@/app/WorkspaceContext';
import { AppShell } from '@/app/layout/AppShell';
import { env } from '@/lib/env';

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * The workspace guard.
 *
 * Design doc §22.1 puts context establishment and access guarding in the route
 * layer. The check that can be made without a round trip is made here: a
 * malformed workspace id never reaches a query, so it never becomes a request
 * the API has to reject.
 */
export function WorkspaceRoute() {
  const { workspaceId } = useParams();

  if (!workspaceId || !UUID.test(workspaceId)) {
    return <Navigate to="/" replace />;
  }

  return (
    <WorkspaceScopeProvider workspaceId={workspaceId} brandId={null}>
      <AppShell />
    </WorkspaceScopeProvider>
  );
}

/** Entry redirect: there is no `/me` endpoint, so the workspace is configured. */
export function RootRedirect() {
  if (!env.workspaceId) return <Navigate to="/not-configured" replace />;
  return <Navigate to={`/w/${env.workspaceId}/portfolio`} replace />;
}

/**
 * `/integrations`: where the OAuth callback sends a dead link (unknown,
 * replayed, expired or mismatched `state`), which names no workspace
 * (ADR-056). Opens the configured workspace's Integrations page carrying
 * only `oauth=invalid_link`, which that page says in words; anything else in
 * the query is dropped, since the address bar is anyone's to type into.
 */
export function IntegrationsRedirect() {
  const [params] = useSearchParams();
  if (!env.workspaceId) return <Navigate to="/not-configured" replace />;
  const search = params.get('oauth') === 'invalid_link' ? '?oauth=invalid_link' : '';
  return <Navigate to={`/w/${env.workspaceId}/integrations${search}`} replace />;
}
